"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.setIpChargeTierSchema = exports.applyIpTierSchema = exports.resolveIpPriceSchema = exports.ipChargeTierEnum = exports.ipServiceSearchQuerySchema = exports.ipRateSourceEnum = void 0;
const zod_1 = require("zod");
/**
 * IP price resolution contracts — ADR-085 Phase 2.
 *
 * The workstation sends CONTEXT, never numbers. There is no `rate`, no
 * `gstPercent` and no `amount` anywhere in these inputs, because "billing never
 * calculates" is only true if the API makes calculating impossible.
 */
exports.ipRateSourceEnum = zod_1.z.enum([
    'STANDARD',
    'BED_CLASS',
    'CORPORATE',
    'CGHS',
    'PACKAGE',
    'PROMOTIONAL',
    'MANUAL_OVERRIDE',
]);
exports.ipServiceSearchQuerySchema = zod_1.z.object({
    /** One box; every identifying attribute is searched at once. */
    term: zod_1.z.string().max(120).optional(),
    departmentId: zod_1.z.string().uuid().optional(),
    accountHeadId: zod_1.z.string().uuid().optional(),
    limit: zod_1.z.coerce.number().int().positive().max(50).default(25),
});
/** ADR-082's four ward tiers — the only rate axis this charge master has. */
exports.ipChargeTierEnum = zod_1.z.enum(['C1', 'C2', 'C3', 'C4']);
exports.resolveIpPriceSchema = zod_1.z.object({
    serviceId: zod_1.z.string().uuid(),
    quantity: zod_1.z.number().int().positive().max(10_000).default(1),
    /** Historic re-pricing resolves the version in effect THEN, not today's. */
    asOfDate: zod_1.z.coerce.date().optional(),
    doctorId: zod_1.z.string().uuid().optional(),
    packageId: zod_1.z.string().uuid().optional(),
    /**
     * An OPERATOR-ASSERTED tier for this one quote, replacing the tier the open
     * bed occupancy would have produced.
     *
     * The resolver has accepted this since the ward-tier work; it simply had no
     * way in from outside, because this schema did not declare it. Omitted is the
     * normal case and still means "derive it from the bed", so the default path
     * is unchanged — a caller must ASK to override, and `apply-tier` (the whole
     * admission at once) has always been able to.
     */
    tier: exports.ipChargeTierEnum.optional(),
    /**
     * THE COUNTER'S OWN RATE, for an OPEN-VALUE service only — integer paise.
     *
     * The same field `captureIpChargeSchema` already carries, declared here so a
     * quote can be asked for the rate the operator has actually typed. Without
     * it the IP grid could show no amount at all on an open-value line: the
     * catalogue answer is the ₹1 sentinel, and the browser is forbidden from
     * multiplying rate by quantity itself (ADR-062 §6A.10), so the AMOUNT cell
     * simply read "—" while the operator watched.
     *
     * REFUSED ON A CATALOGUE-PRICED SERVICE, by the same rule capture applies —
     * a quote that accepted a typed rate where the capture would reject it would
     * show the counter a figure it could never actually charge.
     */
    unitRate: zod_1.z.number().int().positive().optional(),
});
/**
 * Operator-asserted ward tier applied across an admission.
 *
 * `dryRun` returns the same report without writing, so the counter can see the
 * new total — and any "no price at this tier" refusal — before committing.
 */
exports.applyIpTierSchema = zod_1.z.object({
    // The SAME enum the per-line override uses: one list of tiers, so a fifth
    // tier can never exist for one endpoint and not the other.
    tier: exports.ipChargeTierEnum,
    dryRun: zod_1.z.boolean().optional(),
});
/**
 * SET THE STAY'S TIER WITHOUT REPRICING ANYTHING.
 *
 * `applyIpTierSchema` above answers "reprice every uninvoiced line at this
 * tier", which is a different question and — critically — one that CANNOT be
 * asked on a stay with no lines yet. That is precisely the stay whose tier most
 * needs recording: the desk is asked for it BEFORE the first charge, and until
 * this endpoint existed the answer had nowhere to go and was lost on reload.
 *
 * Recording the tier is therefore its own operation, and repricing remains its
 * own. `apply-tier` also records it, so the two can never disagree about what
 * the stay is priced at.
 */
exports.setIpChargeTierSchema = zod_1.z.object({
    tier: exports.ipChargeTierEnum,
});
//# sourceMappingURL=ip-pricing.schema.js.map